import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { resolveExamErrorText } from "../utils/examError";
import { passageRuns } from "../utils/passages";
import { isBlankHtml } from "../components/KaTeXComponent/RichText";

// The current question and the flags of an attempt, kept in sessionStorage so that a reload returns to them.
const storedIndex = (key) => {
  const parsed = Number(sessionStorage.getItem(`${key}:current_index`));
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};
const storedFlags = (key) => {
  try {
    const raw = sessionStorage.getItem(`${key}:flagged`);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

export default function useExamSession(attemptData, submitAnswerFn, examTests = []) {
  const { t } = useTranslation();
  // No key until the attempt has loaded: nothing is read or written before.
  const sessionKey = attemptData?.uuid ? `exam_session:${attemptData.uuid}` : null;

  // Each kept value is paired with the attempt it belongs to. When the attempt arrives (or changes), its own stored
  // values are read in that same render, so the save effects below never write one attempt's storage with values
  // read for another key (which lost the place and the flags on every reload).
  const [place, setPlace] = useState(() => ({ key: sessionKey, index: sessionKey ? storedIndex(sessionKey) : 0 }));
  const [flags, setFlags] = useState(() => ({ key: sessionKey, set: sessionKey ? storedFlags(sessionKey) : new Set() }));
  if (place.key !== sessionKey) setPlace({ key: sessionKey, index: sessionKey ? storedIndex(sessionKey) : 0 });
  if (flags.key !== sessionKey) setFlags({ key: sessionKey, set: sessionKey ? storedFlags(sessionKey) : new Set() });

  const [answeredMap, setAnsweredMap] = useState({});

  const pendingRef = useRef(new Set());

  const questions = useMemo(() => {
    if (!attemptData?.questions) return [];
    return [...attemptData.questions].sort((a, b) => a.order - b.order);
  }, [attemptData]);

  // The kept place is clamped when read, not when stored: before the questions come it stays as it was.
  const maxIndex = Math.max(questions.length - 1, 0);
  const currentIndex = Math.min(place.index, maxIndex);
  const flaggedSet = flags.set;
  const maxIndexRef = useRef(maxIndex);
  maxIndexRef.current = maxIndex;
  const setCurrentIndex = useCallback(
    (next) =>
      setPlace((prev) => {
        const from = Math.min(prev.index, maxIndexRef.current);
        return { ...prev, index: typeof next === "function" ? next(from) : next };
      }),
    []
  );

  useEffect(() => {
    const map = {};
    for (const q of attemptData?.questions || []) {
      if (q.selected_answer_key_uuid) {
        map[q.attempt_question_uuid] = q.selected_answer_key_uuid;
      }
    }
    setAnsweredMap(map);
  }, [attemptData?.questions]);

  useEffect(() => {
    if (place.key) sessionStorage.setItem(`${place.key}:current_index`, String(place.index));
  }, [place]);

  const testSections = useMemo(() => {
    if (!questions.length) return [];

    // Build a uuid -> { order, name } lookup from examTests
    const examTestOrder = {};
    for (const et of examTests) {
      if (et.test?.uuid) {
        examTestOrder[et.test.uuid] = {
          order: et.order ?? Number.MAX_SAFE_INTEGER,
          name: et.test?.name,
        };
      }
    }

    // Group questions by test_uuid, remembering first-appearance index
    const groups = new Map();
    questions.forEach((q, index) => {
      const testUuid = q.test_uuid;
      if (!testUuid) return;
      if (!groups.has(testUuid)) {
        groups.set(testUuid, {
          testUuid,
          testName: q.test_name || examTestOrder[testUuid]?.name || testUuid,
          firstIndex: index,
          questionIndices: [],
        });
      }
      groups.get(testUuid).questionIndices.push(index);
    });

    // Prefer examTests.order if available; fall back to first-appearance index
    const sections = Array.from(groups.values());
    sections.sort((a, b) => {
      const oa = examTestOrder[a.testUuid]?.order;
      const ob = examTestOrder[b.testUuid]?.order;
      if (oa !== undefined && ob !== undefined) return oa - ob;
      return a.firstIndex - b.firstIndex;
    });

    return sections.map(({ firstIndex, ...rest }) => rest);
  }, [questions, examTests]);

  // Context questions: consecutive questions of a test that share a passage form a run, shown next to
  // the passage's text. Only attempts with such questions carry `passages` and `passage_uuid`.
  const passages = useMemo(() => attemptData?.passages ?? {}, [attemptData?.passages]);
  const { runs, runByIndex } = useMemo(() => passageRuns(questions), [questions]);

  useEffect(() => {
    if (flags.key) sessionStorage.setItem(`${flags.key}:flagged`, JSON.stringify([...flags.set]));
  }, [flags]);

  const toggleFlag = useCallback((attemptQuestionUuid) => {
    setFlags((prev) => {
      const next = new Set(prev.set);
      if (next.has(attemptQuestionUuid)) {
        next.delete(attemptQuestionUuid);
      } else {
        next.add(attemptQuestionUuid);
      }
      return { ...prev, set: next };
    });
  }, []);

  const currentQuestion = questions[currentIndex] || null;
  const totalCount = questions.length;
  const answeredCount = Object.keys(answeredMap).length;

  const currentRun = runByIndex[currentIndex] || null;
  // null when the passage did not come with the attempt or has no text: the page says so instead.
  const currentPassage = useMemo(() => {
    const passage = currentRun ? passages[currentRun.passageUuid] : null;
    return passage && !isBlankHtml(passage.body) ? passage : null;
  }, [currentRun, passages]);

  const goToQuestion = useCallback(
    (index) => {
      if (index >= 0 && index < questions.length) {
        setCurrentIndex(index);
      }
    },
    [questions.length, setCurrentIndex]
  );

  const nextQuestion = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  }, [questions.length, setCurrentIndex]);

  const prevQuestion = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, [setCurrentIndex]);

  const selectAnswer = useCallback(
    (attemptQuestionUuid, answerKeyUuid) => {
      if (pendingRef.current.has(attemptQuestionUuid)) return;

      const previousAnswer = answeredMap[attemptQuestionUuid];

      setAnsweredMap((prev) => ({
        ...prev,
        [attemptQuestionUuid]: answerKeyUuid,
      }));

      pendingRef.current.add(attemptQuestionUuid);

      submitAnswerFn({
        attempt_question_uuid: attemptQuestionUuid,
        answer_key_uuid: answerKeyUuid,
      })
        .catch((error) => {
          setAnsweredMap((prev) => {
            const next = { ...prev };
            if (previousAnswer) {
              next[attemptQuestionUuid] = previousAnswer;
            } else {
              delete next[attemptQuestionUuid];
            }
            return next;
          });
          toast.error(
            resolveExamErrorText(t, error, "exam_taking.failed_to_save_answer")
          );
        })
        .finally(() => {
          pendingRef.current.delete(attemptQuestionUuid);
        });
    },
    [answeredMap, submitAnswerFn, t]
  );

  return {
    currentIndex,
    questions,
    currentQuestion,
    answeredMap,
    answeredCount,
    totalCount,
    goToQuestion,
    nextQuestion,
    prevQuestion,
    selectAnswer,
    testSections,
    flaggedSet,
    toggleFlag,
    passages,
    runs,
    runByIndex,
    currentRun,
    currentPassage,
  };
}
